namespace advisorop.components.servicer {

    class ServicerCrudController {
        $router;
        servicer: domains.IServicer;
        user: domains.identity.IUser;
        isWorking = false;
        isLoading = false;

        // Two-factor panel. Loaded separately from the servicer itself: the API that serves it is
        // restricted to the roles that can act on it, so a read-only viewer never asks for it.
        twoFactor: domains.identity.ITwoFactorSetup = {};
        twoFactorLoaded = false;
        laddaTwoFactor = false;

        phoneInput = "";
        phoneError: string = null;
        laddaPhone = false;

        /**
         * The servicer's name as it was last saved. The heading keeps showing this rather than
         * the bound model, so it does not rewrite itself letter by letter while the name is
         * being edited.
         */
        savedName: string;

        constructor(
            private ss: services.ServicerService,
            private is: services.IdentityService,
            private gs: services.GeneralService) {

            this.servicer = new domains.ServicerCreate();

            this.servicer.username = "00000000";
            this.servicer.email = "";
            this.servicer.firstName = "";
            this.servicer.lastName = "";
            this.servicer.role = "SERVICER_OPERATIONS";
        }

        /**
         * The screen is reached both as "create servicer" and, with an id, as the edit screen
         * for an existing one. An id on the model is what tells the two apart.
         */
        isEdit(): boolean {
            return !!(this.servicer && this.servicer.id);
        }

        /**
         * Mirrors the roles the update API accepts. Everyone who can open the servicer list can
         * open this screen, so an operations or trader login gets to read the details without
         * being offered a Save button the server would refuse.
         */
        canEdit(): boolean {
            return !!(this.user &&
                (this.user.role == domains.AdvisoryRoles.SERVICER_ADMIN.toString() ||
                 this.user.role == domains.AdvisoryRoles.SERVICER_LEAD.toString()));
        }

        /**
         * A lead may only look after operations staff - the server enforces this in
         * ServicerController.UpdateServicer, this only keeps the form out of their way.
         */
        canEditServicer(): boolean {
            if (!this.canEdit())
                return false;

            if (this.user.role == domains.AdvisoryRoles.SERVICER_LEAD.toString())
                return this.servicer.role == domains.AdvisoryRoles.SERVICER_OPERATIONS.toString();

            return true;
        }

        /**
         * An administrator who demotes themselves loses this screen, so the server refuses it.
         * Grey the role out rather than letting them find out on save.
         */
        canEditRole(): boolean {
            return this.canEditServicer() && !(this.user && this.servicer && this.user.id == this.servicer.id);
        }

        /**
         * Only an administrator may switch two-factor authentication off; the server enforces this
         * in IdentityService.SetTwoFactorEnabled, this only keeps the action out of the UI.
         */
        isAdmin(): boolean {
            return !!(this.user && this.user.role == domains.AdvisoryRoles.SERVICER_ADMIN.toString());
        }

        loadTwoFactor(): void {
            // Gated on the id, not the username. The model starts life as the blank create form,
            // whose placeholder username is "00000000" - truthy, and a login that does not exist,
            // so asking about it raced the real servicer arriving and lost.
            if (!this.canEdit() || !this.isEdit())
                return;

            this.ss
                .getServicerTwoFactor(this.servicer.username)
                .then(response => {
                    this.twoFactor = response.data || {};
                    this.twoFactorLoaded = true;

                    // Keep the System login summary above in step with the panel below it.
                    this.servicer.twoFactorEnabled = this.twoFactor.twoFactorEnabled;
                }, err => {
                    this.gs.showErrorWithMessage("Two-factor setup could not be loaded! ", err);
                });
        }

        setTwoFactor(enabled: boolean): void {
            if (enabled && !this.twoFactor.canEnable)
                return;

            // Off is always for a stated number of days; the enforcement job switches it back on
            // when they run out, so the administrator is told the date rather than left to
            // remember. See advisorop.identity.TwoFactorExemption for the wording and the range.
            let days: number = null;
            if (!enabled) {
                days = advisorop.identity.TwoFactorExemption.promptForDays(this.savedName);
                if (days === null)
                    return;
            }

            this.laddaTwoFactor = true;
            this.ss
                .setTwoFactorServicerSL(this.servicer, enabled, days)
                .then(() => {
                    toastr.success(enabled
                        ? "Two-factor authentication turned on."
                        : advisorop.identity.TwoFactorExemption.offMessage(this.savedName, days));
                    this.loadTwoFactor();
                }, err => {
                    this.gs.showErrorWithMessage("Two-factor authentication has not been changed! ", err);
                })
                .finally(() => { this.laddaTwoFactor = false; });
        }

        onPhoneChanged(): void {
            this.phoneError = this.phoneInput ? domains.identity.ContactValidator.usPhone(this.phoneInput) : null;
        }

        /** Tidies what was typed into the shape it will be shown in, once it is valid. */
        formatPhoneOnBlur(): void {
            if (this.phoneInput && !domains.identity.ContactValidator.usPhone(this.phoneInput)) {
                this.phoneInput = domains.identity.ContactValidator.formatUsPhone(this.phoneInput);
            }
        }

        savePhone(): void {
            this.phoneError = domains.identity.ContactValidator.usPhone(this.phoneInput);
            if (this.phoneError)
                return;

            this.laddaPhone = true;
            this.ss
                .setServicerPhone(this.servicer.username, this.phoneInput)
                .then(response => {
                    this.twoFactor = response.data || {};
                    this.phoneInput = "";
                    this.phoneError = null;
                    toastr.success("Mobile number has been saved.");
                }, err => {
                    // The server is the authority; show exactly what it said.
                    this.phoneError = (err && err.data) ? err.data : "The number could not be saved.";
                })
                .finally(() => { this.laddaPhone = false; });
        }

        loadServicer(servicerId: string): void {
            this.isLoading = true;
            this.ss
                .getServicer(servicerId)
                .then(response => {
                    this.servicer = response.data;
                    this.savedName = this.servicer.fullName;
                    this.isLoading = false;
                    this.loadTwoFactor();
                }, err => {
                    this.gs.showErrorWithMessage("Servicer could not be loaded! ", err);
                    this.isLoading = false;
                });
        }

        createServicer = () => {
            this.isWorking = true;
            this.ss
                .createServicer(this.servicer)
                .then(response => {
                    //console.log(response);
                    if (response.data.warnings && response.data.warnings.length) {
                        toastr.warning(`New servicer has been created`);
                        for (let i = 0; i < response.data.warnings.length; i++) {
                            toastr.warning(response.data.warnings[i]);
                        }
                    } else {
                        toastr.success("New servicer has been created");
                    }
                    this.isWorking = false;
                    this.$router.navigate(["Dashboard"]);
                }, err => {
                    this.gs.showErrorWithMessage("Servicer has not been created! ", err);
                    this.isWorking = false;
                });
        }

        save(): void {
            this.isWorking = true;
            this.ss
                .updateServicer(this.servicer)
                .then(response => {
                    // Take the saved state back from the server rather than assuming what was
                    // posted is now what is stored - the role in particular is read back there.
                    this.servicer = response.data;
                    this.savedName = this.servicer.fullName;
                    toastr.success("Servicer has been saved");
                    this.isWorking = false;

                    // The email address is itself a place a passcode can be sent, so what the
                    // panel below lists may have just changed.
                    this.loadTwoFactor();
                }, err => {
                    this.gs.showErrorWithMessage("Servicer has not been saved! ", err);
                    this.isWorking = false;
                });
        }

        $onInit() {
            this.is.getUserPromise().then(response => {
                this.user = response;

                // loadTwoFactor is gated on canEdit(), which needs this user. Whichever of the two
                // arrives second is the one that gets to make the call.
                if (!this.twoFactorLoaded)
                    this.loadTwoFactor();
            });
        }

        $routerOnActivate(next, previous) {
            if (next.params.id) {
                this.loadServicer(next.params.id);
            }
        }
    }



    class ServicerCrud implements ng.IComponentOptions {

        public bindings: any;
        public controller: any;
        public controllerAs: string;
        public templateUrl: string;
        public $routeConfig: any;

        constructor() {
            this.bindings = {
                "$router": "<"
            };
            this.controller = ["servicerService", "identityService", "generalService", ServicerCrudController];
            this.controllerAs = "vm";
            this.templateUrl = 'spa/servicer-module/components/servicer-crud.html';
        }
    }
    angular.module(Constants.APP).component("servicerCrud", new ServicerCrud());


}
